import React, { useState, useMemo } from 'react';
import Layout from 'Layout';
import { Helmet } from 'react-helmet-async';
import './style.scss';

import {
  RAW_TRADERS,
  derive,
  series,
  initials,
  money,
  sMoney,
  sPct,
  POS,
  NEG,
  TONE,
  DirectoryView,
  ProfileView,
  TraderCardData,
  ProfileTabType,
} from './component';

const CommunityLeaderBoard: React.FC = () => {
  const [view, setView] = useState<'dir' | 'profile'>('dir');
  const [tab, setTab] = useState<ProfileTabType>('ov');
  const [platform, setPlatform] = useState<string>('all');
  const [stage, setStage] = useState<string>('all');
  const [size, setSize] = useState<string>('all');
  const [sort, setSort] = useState<string>('profit');
  const [q, setQ] = useState<string>('');
  const [layout, setLayout] = useState<'list' | 'grid'>('list');
  const [sel, setSel] = useState<number>(0);

  const ranked = useMemo(() => {
    return RAW_TRADERS.map((t, i) => ({ t, i, profit: (t.cap * t.ret) / 100 })).sort(
      (a, b) => b.profit - a.profit
    );
  }, []);

  const rankOf = useMemo(() => {
    const map: Record<number, number> = {};
    ranked.forEach((x, k) => {
      map[x.i] = k + 1;
    });
    return map;
  }, [ranked]);

  const filteredRows = useMemo(() => {
    let rows = ranked.slice();
    if (platform !== 'all') rows = rows.filter((x) => x.t.platform.toLowerCase() === platform.toLowerCase());
    if (size !== 'all') rows = rows.filter((x) => x.t.cap === +size * 1000);
    if (stage !== 'all') rows = rows.filter((x) => (stage === 'Demo' ? x.t.demo > 0 : x.t.stage === stage));
    if (q.trim()) {
      const query = q.trim().toLowerCase();
      rows = rows.filter(
        (x) =>
          x.t.name.toLowerCase().includes(query) ||
          x.t.tags.some((tag) => tag.toLowerCase().includes(query)) ||
          x.t.platform.toLowerCase().includes(query)
      );
    }
    const cmp: Record<string, (a: (typeof rows)[0], b: (typeof rows)[0]) => number> = {
      profit: (a, b) => b.profit - a.profit,
      ret: (a, b) => b.t.ret - a.t.ret,
      score: (a, b) => b.t.score - a.t.score,
      dd: (a, b) => a.t.dd - b.t.dd,
      days: (a, b) => b.t.days - a.t.days,
    };
    return rows.sort(cmp[sort] || cmp.profit);
  }, [ranked, platform, size, stage, q, sort]);

  const cards: TraderCardData[] = useMemo(() => {
    return filteredRows.map(({ t, i, profit }) => {
      const s = series(t);
      const step = Math.max(1, Math.floor(s.length / 40));
      const sl = s.filter((_, k) => k % step === 0);
      const lo = Math.min(...sl, 0);
      const hi = Math.max(...sl, 0.1);
      return {
        rawIndex: i,
        rank: rankOf[i],
        name: t.name,
        ini: initials(t.name),
        country: t.country,
        stage: t.stage,
        days: t.days,
        score: t.score,
        profitStr: sMoney(profit),
        equityStr: money(t.cap + profit),
        retStr: sPct(t.ret),
        capStr: money(t.cap),
        ddStr: t.dd.toFixed(1) + '%',
        pfStr: t.pf.toFixed(2),
        status: t.status,
        toneBg: TONE[t.tone].bg,
        toneFg: TONE[t.tone].fg,
        retColor: t.ret >= 0 ? POS : NEG,
        medal: rankOf[i] === 1 ? '#E39A2B' : rankOf[i] === 2 ? '#8FA0B4' : rankOf[i] === 3 ? '#B98155' : '#06223B',
        rankBg: rankOf[i] === 1 ? '#FDF7EC' : rankOf[i] === 2 ? '#F5F8FA' : '#FBF6F2',
        spark: sl
          .map((v, k) => (k / (sl.length - 1) * 300).toFixed(1) + ',' + (40 - ((v - lo) / (hi - lo)) * 36).toFixed(1))
          .join(' '),
      };
    });
  }, [filteredRows, rankOf]);

  const podium = useMemo(() => {
    return sort === 'profit' && q.trim() === '' ? cards.slice(0, 3) : [];
  }, [sort, q, cards]);

  const rest = useMemo(() => {
    return podium.length > 0 ? cards.slice(3) : cards;
  }, [podium, cards]);

  const currentTrader = RAW_TRADERS[sel] || RAW_TRADERS[0];
  const derivation = useMemo(() => derive(currentTrader), [currentTrader]);

  const handleSelectTrader = (rawIndex: number) => {
    setSel(rawIndex);
    setView('profile');
    setTab('ov');
  };

  const handleResetFilters = () => {
    setQ('');
    setPlatform('all');
    setStage('all');
    setSize('all');
  };

  return (
    <Layout contentClassName="p-0">
      <Helmet>
        <title>Bảng xếp hạng Capital Manager - LUMIR</title>
      </Helmet>

      <div className="community-leaderboard-page w-full p-4 md:p-6">
        {view === 'dir' && (
          <DirectoryView
            size={size}
            onSizeChange={setSize}
            q={q}
            onQChange={setQ}
            platform={platform}
            onPlatformChange={setPlatform}
            stage={stage}
            onStageChange={setStage}
            sort={sort}
            onSortChange={setSort}
            layout={layout}
            onLayoutChange={setLayout}
            cards={cards}
            podium={podium}
            rest={rest}
            totalFilteredCount={filteredRows.length}
            onSelectTrader={handleSelectTrader}
            onResetFilters={handleResetFilters}
          />
        )}

        {view === 'profile' && (
          <ProfileView
            trader={currentTrader}
            derivation={derivation}
            tab={tab}
            onTabChange={setTab}
            onBack={() => setView('dir')}
          />
        )}
      </div>
    </Layout>
  );
};

export default CommunityLeaderBoard;
